import { useState, useEffect, useContext, useCallback } from 'react';
import { TeamController, UserController } from 'api';
import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { useDebounce } from 'hooks';

export interface Team {
  _id: string;
  id: string;
  name: string;
  type: 'team';
  userCount: number;
  teamAdminCount: number;
  createdAt: string;
  orgId: string;
  updatedAt: string;
  teamMlsId?: string;
}

const useGetTeams = ({ shouldFetch = true, searchText = '', orgId = '' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const debouncedSearch = useDebounce(searchText, 500);

  const fetchTeams = async () => {
    try {
      setIsLoading(true);
      const teamList = await TeamController.getTeamsWithUserCount();
      setAllTeams(teamList);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error loading teams');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldFetch) return;
    fetchTeams();
  }, [shouldFetch]);

  const filterTeams = useCallback(
    async (text: string) => {
      setIsLoading(true);

      try {
        const { users } = await UserController.search({
          name: text,
          orgId,
          page: 0,
          excludeInternalOrgs: true,
        });

        const teamIds = users.map(user => user.team);

        const matchByTeamName = allTeams.filter(team =>
          team.name.toLowerCase().trim().includes(text.toLowerCase().trim()),
        );
        const foundTeamIds = new Set(matchByTeamName.map(match => match.id));
        const matchByUser = allTeams.filter(
          team => !foundTeamIds.has(team.id) && teamIds.includes(team.id),
        );

        setTeams([...matchByTeamName, ...matchByUser]);
      } catch (err) {
        errorReporter.error(err);
        message.error('Error searching for team');
      } finally {
        setIsLoading(false);
      }
    },
    [allTeams, orgId],
  );

  useEffect(() => {
    if (!shouldFetch) return;
    if (debouncedSearch) {
      filterTeams(debouncedSearch);
      return;
    }
    setTeams(allTeams);
  }, [debouncedSearch, allTeams, shouldFetch, filterTeams]);

  return { isLoading, fetchTeams, teams, teamsCount: teams.length };
};

export default useGetTeams;
